import { find, isEmpty } from 'lodash'
import moment from 'moment'
import { getCartId, createNewCartId, createGUID } from '@utils/shopping-cart'
import API from '@/api/base'
import store from '@state/store'

API.define(
  'applyCode',
  {
    cartId: '',
    code: '',
    platform: '',
    siteId: '',
  },
  {
    collectionPath: 'shopping_carts/apply_code',
  }
)

API.define(
  'removeCode',
  {
    cartId: '',
    platform: '',
    siteId: '',
  },
  {
    collectionPath: 'shopping_carts/remove_code',
  }
)

export const state = {
  shoppingCart: { items: [] },
  shoppingCartId: getCartId(),
}

export const getters = {
  getDiscount: ({ shoppingCart }) => shoppingCart.discount,
  getPromoMessage: ({ shoppingCart }) => {
    if (shoppingCart.promotion) {
      return shoppingCart.promotion.message
    }
    return ''
  },
  getPromo: ({ shoppingCart }) => {
    if (shoppingCart.promotion) {
      return shoppingCart.promotion
    }
    return null
  },
  getShippingFee: ({ shoppingCart }) => shoppingCart.shippingFee,
  getShoppingCart: ({ shoppingCart }) => shoppingCart,
  getShoppingCartId: ({ shoppingCartId }) => shoppingCartId,
  getShoppingCartItems: ({ shoppingCart }) => shoppingCart.items,
  getShoppingCartSubscriptions: ({ shoppingCart }) =>
    shoppingCart.items.filter((i) => i.subscription),
  getShoppingCartNonSubscriptions: ({ shoppingCart }) =>
    shoppingCart.items.filter(
      (i) =>
        !i.subscription ||
        !Object.prototype.hasOwnProperty.call(i, 'subscription')
    ),
  getSubtotal: ({ shoppingCart }) => shoppingCart.subtotal,
  // sum each item in the cart by price * quantity, default value of 0.0
  getSubscriptionSubtotal: ({ shoppingCart }) =>
    shoppingCart.items
      .filter((i) => i.subscription === true)
      .reduce((sum, i) => sum + i.price * i.quantity, 0.0),
  // sum each item in the cart by tax * quantity, default value of 0.0
  // TODO(Dylan): Need to selectively apply tax based on the item's applyTax value
  getTax: ({ shoppingCart }) => shoppingCart.tax,
  // shoppingCart.items
  //   .filter((i) => i.applyTax === true)
  //   .reduce((sum, i) => sum + i.tax * i.quantity, 0.0),
  getTotal: ({ shoppingCart }) => shoppingCart.total,
  getImageForShoppingCartItem: (state, rootGetters) => (itemId) => {
    const cartItem = find(state.shoppingCart.items, { id: itemId })
    const shopCategories = rootGetters['shopCategories/getShopCategories']
    let image
    shopCategories.forEach((category) => {
      const shopItem = find(category.shopItems, {
        id: cartItem.shopItemId,
      })
      if (shopItem) {
        image = shopItem.images
      }
    })
    return image
  },
  getCartLength: ({ shoppingCart }) => {
    if (shoppingCart && shoppingCart.items) {
      return shoppingCart.items.reduce((acc, i) => acc + i.quantity, 0)
    } else {
      return 0
    }
  },
}

export const actions = {
  addItemToCart: ({ commit, state, dispatch }, params) =>
    new Promise((resolve, reject) => {
      const itemParams = {
        name: params.item.name,
        desc: params.item.desc,
        maxQuantity: params.item.maxQuantity,
        price: params.item.price,
        subscription: params.item.subscription,
        subscriptionEnd: null,
        subscriptionStart: null,
        shopItem: {
          id: params.item.id,
        },
        options: params.options,
      }
      if (params.item.subscription === true) {
        itemParams.subscriptionStart = moment(
          params.item.subscriptionStart
        ).unix()
        itemParams.subscriptionEnd = moment(params.item.subscriptionEnd).unix()
      }
      if (state.shoppingCart) {
        API.one('shoppingCart', state.shoppingCart.id)
        .all('item')
        .post(itemParams, {
          include: 'shop_item_images',
        })
        .then((response) => {
          commit('INIT_SHOPPING_CART')
          commit('ADD_SHOPPING_CART_ITEM', response.data)
          resolve(response.data)
        })
        .catch((error) => {
          reject(error.response)
        })
      } else {
        store
        .dispatch('shoppingCart/createShoppingCart')
        .then(() => {
          API.one('shoppingCart', state.shoppingCart.id)
          .all('item')
          .post(itemParams, {
            include: 'shop_item_images',
          })
          .then((response) => {
            commit('INIT_SHOPPING_CART')
            commit('ADD_SHOPPING_CART_ITEM', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            reject(error.response)
          })
        })
      }
    }),

  applyPromoCode: ({ commit, dispatch }, params) =>
    new Promise((resolve, reject) => {
      API.create('applyCode', params)
        .then((response) => {
          resolve(response.data)
          dispatch('fetchShoppingCart')
        })
        .catch((error) => {
          reject(error)
        })
    }),

  removePromoCode: ({ commit, dispatch }, params) =>
    new Promise((resolve, reject) => {
      API.create('removeCode', params)
        .then((response) => {
          resolve(response.data)
          dispatch('fetchShoppingCart')
        })
        .catch((error) => {
          reject(error)
        })
    }),

  updateItemQuantity: ({ commit }, payload) => {
    commit('UPDATE_ITEM_QUANTITY', payload)
  },

  createCartId: (_context, cart) => {
    createNewCartId(cart.id, cart.cookie)
  },

  createShoppingCart: ({ commit, state, dispatch, rootGetters }) =>
    new Promise((resolve, reject) => {
      if (!isEmpty(state.shoppingCart)) {
        resolve(state.shoppingCart)
      }
      const cartId = createGUID()
      API.create(
        'shoppingCart',
        {
          cookie: cartId,
          site: {
            id: rootGetters['sites/getSite'].id,
          },
        },
        {
          include: 'items',
        }
      )
        .then((response) => {
          dispatch('createCartId', response.data)
          commit('INIT_SHOPPING_CART', response.data)
          commit('SET_SHOPPING_CART', response.data)
          commit('SET_CART_ID', response.data.id)
          dispatch('fetchShoppingCart')
          resolve(response.data)
        })
        .catch((error) => {
          reject(error.response)
        })
    }),

  fetchShoppingCart: ({ state, commit }) =>
    new Promise((resolve, reject) => {
      API.find('shoppingCart', state.shoppingCartId, {
        include: 'items',
      })
        .then((response) => {
          commit('INIT_SHOPPING_CART', response.data)
          commit('SET_SHOPPING_CART', response.data)
          resolve(response.data)
        })
        .catch((error) => {
          reject(error.response)
        })
    }),

  updateShoppingCart: ({ dispatch, state }, items) =>
    new Promise((resolve, reject) => {
      state.shoppingCart.items.forEach((item) => {
        API.one('shoppingCart', state.shoppingCart.id)
          .one('item', item.id)
          .patch({
            id: item.id,
            name: item.name,
            desc: item.desc,
            quantity: item.quantity,
          })
          .then(() => {
            dispatch('fetchShoppingCart')
          })
      })
      resolve()
    }),

  removeShoppingCartItem: ({ commit, dispatch }, item) =>
    new Promise((resolve, reject) => {
      API.one('shoppingCart', state.shoppingCart.id)
        .one('item', item.id)
        .destroy()
        .then(() => {
          commit('REMOVE_SHOPPING_CART_ITEM', item)
          dispatch('fetchShoppingCart')
        })
        .catch(() => {})
    }),

  generateNewCart: ({ commit, rootGetters, dispatch }) =>
    new Promise((resolve, reject) => {
      const cartId = createGUID()
      API.create(
        'shoppingCart',
        {
          cookie: cartId,
          site: { id: rootGetters['sites/getSite'].id },
        },
        { include: 'items' }
      )
        .then((response) => {
          // dispatch('createCartId', response.data)
          commit('RESET_SHOPPING_CART', { items: [] })
          commit('SET_CART_ID', response.data.id)
          commit('INIT_SHOPPING_CART', response.data)
          dispatch('fetchShoppingCart')
          resolve(response.data)
        })
        .catch((error) => {
          reject(error.response)
        })
    }),
}

export const mutations = {
  ADD_SHOPPING_CART_ITEM(state, item) {
    const itemInCart = find(state.shoppingCart.items, { id: item.id })
    if (itemInCart) {
      itemInCart.quantity += 1
    } else {
      item.quantity = 1
      state.shoppingCart.items.push(item)
    }
  },

  UPDATE_ITEM_QUANTITY(state, { itemId, quantity }) {
    const itemInCart = find(state.shoppingCart.items, { id: itemId })
    itemInCart.quantity = parseInt(quantity)
  },

  REMOVE_SHOPPING_CART_ITEM(state, item) {
    const itemInCartIndex = state.shoppingCart.items.findIndex(
      (i) => i.id === item.id
    )
    state.shoppingCart.items.splice(itemInCartIndex, 1)
  },

  SET_SHOPPING_CART(state, cart) {
    state.shoppingCart = cart
  },

  INIT_SHOPPING_CART(state, cart) {
    if (!state.shoppingCart) {
      state.shoppingCart = cart
    }
  },

  RESET_SHOPPING_CART(state, cart) {
    state.shoppingCart = cart
  },

  SET_CART_ID(state, payload) {
    state.shoppingCartId = payload
  },
}
